import React from 'react';
import Section from '@hero/ui-kit/layout/Section';
import P from '@hero/ui-kit/typography/P';
import useTheme from '@hero/branding/theme';
import { MarkButton } from '../../../components/MarkControls';
import type { SegmentFilter, SegmentAttribute } from '../../../types';
import { OPERATOR_LABELS } from './OperatorSelect';
import { stripEndOfDay, validateSegmentFilters } from './validation';
import * as styles from './style.module.css';

type Props = {
    filter: SegmentFilter;
    index: number;
    attributes: SegmentAttribute[];
    onEdit: () => void;
    onRemove: (index: number) => void;
    idPrefix: string;
};

const FilterRow: React.FC<Props> = ({ filter, index, attributes, onEdit, onRemove, idPrefix }) => {
    const { colors, borders } = useTheme();
    const attribute = attributes.find((candidate) => candidate.name === filter.attribute);
    const errors = validateSegmentFilters([filter], attributes);
    const condition =
        filter.operator === 'is_null'
            ? filter.values[0] === false
                ? 'is not empty'
                : 'is empty'
            : OPERATOR_LABELS[filter.operator];
    const values =
        filter.operator === 'is_null'
            ? ''
            : filter.values
                  .map((value) =>
                      typeof value === 'boolean' ? (value ? 'True' : 'False') : stripEndOfDay(value)
                  )
                  .join(filter.operator === 'between' ? ' to ' : ', ');

    return (
        <article data-filter-row={index} aria-label={`Filter ${index + 1}`}>
            <Section
                border
                padding="small"
                className={styles.filterCard}
                styles={{ ...borders.variant.secondary, padding: '1.6rem' }}
            >
                <div className={styles.filterCopy}>
                    <P
                        noDefaultMargin
                        size="small"
                        styles={{ color: colors.neutrals.textBeta, marginBottom: '0.4rem' }}
                    >
                        {`Filter ${index + 1}`}
                    </P>
                    <P noDefaultMargin>
                        <strong>
                            {attribute?.label ?? (filter.attribute || 'Choose a field')}
                        </strong>{' '}
                        {condition}
                        {values && (
                            <>
                                {' '}
                                <strong>{values}</strong>
                            </>
                        )}
                    </P>
                    {errors.length > 0 && (
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{ color: colors.status.fail.alpha, marginTop: '0.4rem' }}
                        >
                            {errors[0].message}
                        </P>
                    )}
                </div>
                <div className={styles.actions}>
                    <MarkButton
                        id={`${idPrefix}-${index}-edit`}
                        onClick={onEdit}
                        aria-label={`Edit filter ${index + 1}`}
                        variant="outlineSecondary"
                    >
                        Edit
                    </MarkButton>
                    <MarkButton
                        onClick={() => onRemove(index)}
                        aria-label={`Remove filter ${index + 1}`}
                        variant="secondary"
                    >
                        Remove
                    </MarkButton>
                </div>
            </Section>
        </article>
    );
};

export default FilterRow;
